import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Heart, X, MessageCircle, Settings, Sparkles, MapPin, Trophy, Search, Users, RefreshCw, Filter, UserPlus, CheckCircle, Gamepad2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import NotificationBell from '../components/NotificationBell';
import GamificationPopup from '../components/GamificationPopup';
import ProfileVideoPlayer from '../components/ProfileVideoPlayer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Discover = () => {
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [swiping, setSwiping] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [showPopup, setShowPopup] = useState(null);
  const [gamificationStats, setGamificationStats] = useState(null);

  useEffect(() => {
    // Capture referral code from URL if present (from redirect after OAuth)
    const params = new URLSearchParams(window.location.search);
    const refCode = params.get('referral_code') || params.get('ref');
    if (refCode) {
      // Store in localStorage for the session processing
      localStorage.setItem('referral_code', refCode);
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    }
    loadProfiles();
  }, []);

  const loadProfiles = async () => {
    try {
      const response = await axios.get(`${API}/discover`, { withCredentials: true });
      if (response.data.length === 0) {
        // No more profiles available
        toast.info("You've seen all available profiles! Check back later for new users.");
        setProfiles([]);
      } else {
        setProfiles(response.data);
      }
      setLoading(false);
    } catch (e) {
      console.error('Failed to load profiles:', e);
      if (e.response?.status === 403 || e.response?.data?.detail?.includes('age') || e.response?.data?.detail?.includes('gender')) {
        toast.info('Please complete your profile first');
        navigate('/setup');
      } else {
        toast.error('Failed to load profiles');
      }
      setLoading(false);
    }
  };

  const handleSwipe = async (action) => {
    if (swiping || currentIndex >= profiles.length) return;
    
    setSwiping(true);
    const profile = profiles[currentIndex];

    try {
      const response = await axios.post(`${API}/swipe`, {
        target_user_id: profile.id,
        action: action
      }, { withCredentials: true });

      if (response.data.matched) {
        toast.success(`It's a match with ${profile.name}! 💕`);
      } else if (response.data.friend_request_sent) {
        toast.success(`Friend request sent to ${profile.name}! 👥`);
      }

      // Check for gamification pop-up triggers
      if (response.data.popup_trigger) {
        setShowPopup(response.data.popup_trigger);
        // Load gamification stats
        const statsRes = await axios.get(`${API}/gamification/stats`, { withCredentials: true });
        setGamificationStats(statsRes.data);
      }

      setCurrentIndex(currentIndex + 1);
      
      // Don't auto-reload - let user see "No More Profiles" screen with reset option
      // They can manually reset swipes when they've gone through everyone
    } catch (e) {
      if (e.response?.status === 403 && e.response?.data?.detail?.includes('cooldown')) {
        toast.error('You are in cooldown. Pay $10 or wait to continue.');
      } else {
        toast.error('Failed to process swipe');
      }
    } finally {
      setTimeout(() => setSwiping(false), 300);
    }
  };

  const resetSwipes = async () => {
    if (!window.confirm('Reset all your swipes? You will be able to swipe on everyone again.')) {
      return;
    }

    setResetting(true);
    try {
      const response = await axios.post(`${API}/swipes/reset`, {}, { withCredentials: true });
      toast.success(response.data.message || 'Swipes reset! Loading profiles...');
      
      // Reset state first
      setProfiles([]);
      setCurrentIndex(0);
      setLoading(true);
      
      // Small delay to ensure backend has processed the reset
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Load fresh profiles
      const profilesResponse = await axios.get(`${API}/discover`, { withCredentials: true });
      if (profilesResponse.data.length > 0) {
        setProfiles(profilesResponse.data);
        toast.success(`Found ${profilesResponse.data.length} profiles to discover!`);
      } else {
        toast.info("No profiles available right now. Check back later!");
        setProfiles([]);
      }
      setLoading(false);
    } catch (e) {
      console.error('Reset failed:', e);
      toast.error('Failed to reset swipes');
      setLoading(false);
    } finally {
      setResetting(false);
    }
  };

  const handleShare = async () => {
    console.log('handleShare called');
    console.log('gamificationStats:', gamificationStats);
    
    if (!gamificationStats?.referral_code) {
      toast.error('No referral code found');
      return;
    }

    // Use the share preview endpoint for better social media previews
    const sharePreviewUrl = `${BACKEND_URL}/api/share/ref/${gamificationStats.referral_code}`;
    const shareText = `Join me on HeartLink and find your perfect match! 💕`;
    
    console.log('Share preview URL:', sharePreviewUrl);
    
    // Facebook share URL
    const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(sharePreviewUrl)}&quote=${encodeURIComponent(shareText)}`;
    
    console.log('Facebook share URL:', facebookShareUrl);
    
    // Track the share
    try {
      await axios.post(`${API}/gamification/share`, null, {
        params: { share_type: showPopup },
        withCredentials: true
      });
      
      console.log('Opening Facebook share dialog...');
      
      // Open Facebook share dialog
      const shareWindow = window.open(facebookShareUrl, '_blank', 'width=600,height=400');
      
      if (shareWindow) {
        toast.success('Share link opened! Share to win prizes 🎁');
      } else {
        toast.error('Pop-up blocked! Please allow pop-ups to share.');
      }
      
      setShowPopup(null);
    } catch (e) {
      console.error('Share error:', e);
      toast.error('Failed to track share');
    }
  };

  const handlePayment = async () => {
    try {
      await axios.post(`${API}/gamification/payment`, {
        payment_method: 'mock_payment',
        amount: 10
      }, { withCredentials: true });
      
      toast.success('Payment successful! You now have unlimited access 🎉');
      setShowPopup(null);
    } catch (e) {
      toast.error('Payment failed');
    }
  };

  const handleDeclinePayment = async () => {
    try {
      await axios.post(`${API}/gamification/decline-payment`, {}, { withCredentials: true });
      
      toast.info('You can return in 3 days. See you soon! ⏰');
      setShowPopup(null);
    } catch (e) {
      toast.error('Failed to set cooldown');
    }
  };

  const changePreference = async (newPreference) => {
    try {
      await axios.put(`${API}/profile`, {
        looking_for: newPreference
      }, { withCredentials: true });
      
      toast.success(`Preference updated to: ${newPreference === 'everyone' ? 'Everyone' : newPreference}`);
      setShowPreferences(false);
      setCurrentIndex(0);
      await loadProfiles();
    } catch (e) {
      console.error('Failed to update preference:', e);
      toast.error('Failed to update preference');
    }
  };

  const currentProfile = profiles[currentIndex];

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-pink-400 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div 
            className="flex items-center space-x-2 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => window.location.reload()}
            title="Refresh page"
          >
            <Heart className="w-6 h-6 text-pink-500 fill-pink-500" />
            <span className="text-xl font-bold bg-gradient-to-r from-pink-500 to-purple-600 bg-clip-text text-transparent">Heartlink</span>
          </div>
          <div className="flex items-center space-x-3">
            <Button
              data-testid="settings-btn"
              onClick={() => navigate('/settings')}
              variant="ghost"
              size="icon"
              className="hover:bg-purple-100 rounded-full"
            >
              <Settings className="w-5 h-5 text-purple-500" />
            </Button>
            <Button
              data-testid="feed-btn"
              onClick={() => navigate('/feed')}
              variant="ghost"
              size="icon"
              className="hover:bg-green-100 rounded-full"
            >
              <Users className="w-5 h-5 text-green-500" />
            </Button>
            <Button
              data-testid="leaderboard-btn"
              onClick={() => navigate('/leaderboard')}
              variant="ghost"
              size="icon"
              className="hover:bg-purple-100 rounded-full"
            >
              <Trophy className="w-5 h-5 text-purple-500" />
            </Button>
            <Button
              data-testid="matches-btn"
              onClick={() => navigate('/matches')}
              variant="ghost"
              size="icon"
              className="hover:bg-pink-100 rounded-full"
            >
              <MessageCircle className="w-5 h-5 text-pink-500" />
            </Button>
            <Button
              data-testid="games-btn"
              onClick={() => navigate('/games')}
              variant="ghost"
              size="icon"
              className="hover:bg-purple-100 rounded-full"
              title="Couples Games"
            >
              <Gamepad2 className="w-5 h-5 text-purple-500" />
            </Button>
            <NotificationBell />
            <Button
              data-testid="search-btn"
              onClick={() => navigate('/search')}
              variant="ghost"
              size="icon"
              className="hover:bg-blue-100 rounded-full"
            >
              <Search className="w-5 h-5 text-blue-500" />
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-md mx-auto px-4 py-8">
        {!currentProfile ? (
          <div className="text-center py-20">
            <div className="w-20 h-20 bg-gradient-to-br from-pink-400 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <Heart className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-700 mb-2">🎉 You've Seen All Profiles!</h2>
            <p className="text-gray-500 mb-2">You went through all {profiles.length > 0 ? profiles.length : 'available'} profiles.</p>
            <p className="text-gray-400 text-sm mb-6">Reset your swipes to see everyone again or check back later for new users.</p>
            
            {/* Options */}
            <div className="space-y-3 max-w-sm mx-auto">
              <Button
                onClick={resetSwipes}
                disabled={resetting}
                className="w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white rounded-full py-6 text-lg shadow-lg"
              >
                <RefreshCw className={`w-5 h-5 mr-2 ${resetting ? 'animate-spin' : ''}`} />
                {resetting ? 'Resetting...' : '🔄 Reset & Start Over'}
              </Button>
              
              <Button
                onClick={() => setShowPreferences(!showPreferences)}
                variant="outline"
                className="w-full border-2 border-purple-300 text-purple-600 hover:bg-purple-50 rounded-full py-6 text-lg"
              >
                <Filter className="w-5 h-5 mr-2" />
                Change Preferences
              </Button>

              {showPreferences && (
                <div className="bg-white rounded-2xl p-4 shadow-lg space-y-2">
                  <p className="text-sm text-gray-600 mb-3">Looking for:</p>
                  <Button
                    onClick={() => changePreference('women')}
                    variant="outline"
                    className="w-full hover:bg-pink-50 border-pink-200"
                  >
                    Women
                  </Button>
                  <Button
                    onClick={() => changePreference('men')}
                    variant="outline"
                    className="w-full hover:bg-blue-50 border-blue-200"
                  >
                    Men
                  </Button>
                  <Button
                    onClick={() => changePreference('everyone')}
                    variant="outline"
                    className="w-full hover:bg-purple-50 border-purple-200"
                  >
                    Everyone
                  </Button>
                </div>
              )}
              
              <Button
                onClick={loadProfiles}
                variant="ghost"
                className="w-full text-gray-600 hover:text-gray-800"
              >
                Check for New Profiles
              </Button>
            </div>
          </div>
        ) : (
          <div>
            {/* Profile Card */}
            <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden" style={{height: '600px'}}>
              <div className="relative h-full">
                {/* Video or Photo */}
                {currentProfile.profile_video_url ? (
                  <div className="h-full cursor-pointer" onClick={() => navigate(`/profile/${currentProfile.id}`)}>
                    <ProfileVideoPlayer 
                      videoUrl={currentProfile.profile_video_url.startsWith('http') 
                        ? currentProfile.profile_video_url 
                        : `${BACKEND_URL}${currentProfile.profile_video_url}`}
                      className="h-full"
                      autoPlay={true}
                    />
                  </div>
                ) : (
                  <img
                    src={currentProfile.photos?.[0]?.startsWith('http') ? currentProfile.photos[0] : (currentProfile.photos?.[0] ? `${BACKEND_URL}${currentProfile.photos[0]}` : 'https://via.placeholder.com/400x600?text=No+Photo')}
                    alt={currentProfile.name}
                    className="w-full h-full object-cover cursor-pointer"
                    onClick={() => navigate(`/profile/${currentProfile.id}`)}
                  />
                )}
                
                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent pointer-events-none"></div>

                {/* AI Score Badge */}
                <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-full flex items-center space-x-2 shadow-lg">
                  <Sparkles className="w-4 h-4 text-purple-500" />
                  <span className="font-bold text-purple-600">{currentProfile.compatibility_score}% Match</span>
                </div>

                {/* View Profile Button */}
                <button
                  data-testid="view-profile-btn"
                  onClick={() => navigate(`/profile/${currentProfile.id}`)}
                  className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-semibold text-gray-700 hover:bg-white shadow-lg"
                  style={{transition: 'background-color 0.2s ease'}}
                >
                  View Profile
                </button>

                {/* Info */}
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <div className="flex items-baseline space-x-3 mb-2">
                    <h2 className="text-3xl font-bold">{currentProfile.name}</h2>
                    {currentProfile.verification_status === 'approved' && (
                      <div className="flex items-center space-x-1 bg-blue-500/80 backdrop-blur-sm px-2 py-1 rounded-full">
                        <CheckCircle className="w-4 h-4 text-white" />
                        <span className="text-xs font-semibold text-white">Verified</span>
                      </div>
                    )}
                    <span className="text-xl">{currentProfile.age}</span>
                  </div>
                  
                  {currentProfile.location && (
                    <div className="flex items-center space-x-1 text-sm mb-3">
                      <MapPin className="w-4 h-4" />
                      <span>{currentProfile.location}</span>
                    </div>
                  )}

                  <p className="text-sm mb-3 line-clamp-2">{currentProfile.bio}</p>

                  {currentProfile.interests?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {currentProfile.interests.slice(0, 5).map((interest, idx) => (
                        <Badge key={idx} className="bg-white/20 backdrop-blur-sm text-white border-white/30">
                          {interest}
                        </Badge>
                      ))}
                    </div>
                  )}

                  {currentProfile.compatibility_reason && (
                    <div className="bg-purple-500/20 backdrop-blur-sm rounded-xl p-3 border border-white/30">
                      <p className="text-xs font-medium">{currentProfile.compatibility_reason}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-center items-center space-x-4 mt-8">
              <Button
                data-testid="pass-btn"
                onClick={() => handleSwipe('pass')}
                disabled={swiping}
                size="icon"
                className="w-16 h-16 rounded-full bg-white hover:bg-red-50 shadow-xl border-2 border-red-200 hover:border-red-400"
              >
                <X className="w-7 h-7 text-red-500" />
              </Button>
              
              <Button
                data-testid="friend-btn"
                onClick={() => handleSwipe('friend')}
                disabled={swiping}
                size="icon"
                className="w-16 h-16 rounded-full bg-white hover:bg-blue-50 shadow-xl border-2 border-blue-200 hover:border-blue-400"
                title="Add as Friend"
              >
                <UserPlus className="w-7 h-7 text-blue-500" />
              </Button>
              
              <Button
                data-testid="like-btn"
                onClick={() => handleSwipe('like')}
                disabled={swiping}
                size="icon"
                className="w-20 h-20 rounded-full bg-gradient-to-br from-pink-500 to-red-500 hover:from-pink-600 hover:to-red-600 shadow-2xl transform hover:scale-110"
              >
                <Heart className="w-9 h-9 text-white fill-white" />
              </Button>
            </div>

            {/* Counter */}
            <div className="text-center mt-6 text-gray-500 text-sm">
              {currentIndex + 1} of {profiles.length} profiles
            </div>
          </div>
        )}
      </div>

      {/* Gamification Pop-ups */}
      {showPopup && (
        <GamificationPopup
          type={showPopup}
          onClose={() => setShowPopup(null)}
          onShare={handleShare}
          onPay={handlePayment}
          onDecline={handleDeclinePayment}
          referralLink={gamificationStats?.referral_code ? `${window.location.origin}/?ref=${gamificationStats.referral_code}` : ''}
          prizeImage={gamificationStats?.prize_image ? `${BACKEND_URL}${gamificationStats.prize_image}` : null}
          gamificationStats={gamificationStats}
        />
      )}
    </div>
  );
};

export default Discover;
